import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputTextarea} from "primereact/inputtextarea";
import {Tooltip} from "primereact/tooltip";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {selectReadOnly} from "../../redux/persistenceSlice";

/**
 * Comment icon to show a comment for a parameter.
 * @param {RevaValueParameterType} parameterType the type of the parameter
 * @param {boolean} open flag, if the comment dialog is open
 * @param {function} onOpen function to open the comment dialog
 * @param {function} onClose function to close the comment dialog
 * @param {function} onConfirm function to confirm the comment
 * @param {function|null} onReset function to reset the value
 * @param {function|null} onClear function to clear the comment
 * @param {string|null} comment comment text
 * @param {string} label label of the parameter
 * @param {boolean} required
 * @return {React.ReactNode}
 **/
export default function CommentIcon({
    parameterType,
    open,
    onOpen,
    onClose,
    onConfirm,
    onReset,
    onClear,
    comment,
    label,
    required,
}) {
    const {t} = useTranslation();
    const ref = useRef(null);
    const textareaRef = useRef(null);

    const [mounted, setMounted] = useState(false);
    const [commentValue, setCommentValue] = useState(comment || "");
    const readOnly = useSelector(selectReadOnly);

    useEffect(() => {
        if (ref.current && !mounted) {
            setMounted(true); // Trigger a re-render when ref is assigned
        }
    }, [mounted]);

    const disabled = comment === undefined || comment === null;

    useEffect(() => {
        setCommentValue(comment || "");
    }, [comment]);

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                textareaRef.current?.focus();
                const length = textareaRef.current?.value.length || 0;
                textareaRef.current?.setSelectionRange(length, length);
            }, 20);
        } else {
            setCommentValue(comment || "");
        }
    }, [open, comment]);

    if (!comment && readOnly) return null;

    if (parameterType === "META" || parameterType === "DOC_PROPERTY") return null;

    const editabledDialog = () => (
        <Dialog
            id="commentDialog"
            visible={open}
            draggable={true}
            dismissableMask={!required}
            style={{width: "50vw"}}
            onHide={() => onClose?.()}
            pt={{
                content: {
                    className: "p-6 pt-0",
                },
                headerTitle: {
                    className: "font-normal",
                },
                closeButton: {
                    className: "hidden",
                },
            }}
            header={t(required ? "object.commentDialogTitleChangedValue" : "object.commentDialogTitleGeneral", {
                input: label,
            })}
            footer={
                <>
                    {!!comment && !required && !!onClear && (
                        <Button
                            className="p-button-text"
                            label={t("general.clear")}
                            tabIndex={4}
                            icon="pi pi-eraser"
                            onClick={() => {
                                onClear();
                                onClose?.();
                            }}
                        />
                    )}
                    <Button
                        className="p-button-text"
                        label={t("general.cancel")}
                        icon="pi pi-times"
                        tabIndex={3}
                        onClick={() => {
                            if (onReset != null) onReset();
                            onClose?.();
                        }}
                    />
                    <Button
                        className="ml-2"
                        label={t("general.save")}
                        icon="pi pi-check"
                        tabIndex={2}
                        disabled={!commentValue || commentValue.toString().trim().length === 0}
                        onClick={() => {
                            onConfirm?.(commentValue);
                            onClose?.();
                        }}
                        autoFocus
                    />
                </>
            }
        >
            <InputTextarea
                ref={textareaRef}
                className="w-full"
                value={commentValue || ""}
                tabIndex={1}
                rows={5}
                onChange={(e) => setCommentValue(e.target.value || null)}
            />
        </Dialog>
    );

    const readonlyDialog = () => (
        <Dialog
            id="commentDialog"
            visible={open}
            draggable={true}
            dismissableMask={true}
            style={{width: "50vw"}}
            onHide={() => onClose?.()}
            pt={{
                content: {
                    className: "p-6 pt-0",
                },
                headerTitle: {
                    className: "font-normal",
                },
            }}
            header={t("object.commentDialogTitleReadOnly", {input: label})}
            footer={
                <Button
                    className="p-button-text"
                    label={t("general.close")}
                    icon="pi pi-times"
                    onClick={() => {
                        onClose?.();
                    }}
                />
            }
        >
            <InputTextarea ref={textareaRef} className="w-full" value={commentValue || ""} disabled={true} rows={5} />
        </Dialog>
    );

    return (
        <>
            {readOnly ? readonlyDialog() : editabledDialog()}
            <Tooltip target={ref.current} className="custom-tooltip" disabled={disabled} />
            <i
                ref={ref}
                onClick={() => onOpen?.()}
                className={`pi pi-comment tooltip-icon cursor-pointer transition-colors ${
                    disabled ? "text-gray-500" : "text-blue-500"
                }`}
                data-pr-tooltip={comment}
                data-pr-position="top"
            />
        </>
    );
}
